import { IDetailsRowProps, MessageBarType } from '@fluentui/react';
import {
  CommonStyles,
  detailsHeaderStylesBasic,
  DetailsList,
  detailsListStylesCard,
  EmptyMessage,
  IColumnWithDataType,
  IDetailsListProps,
  Info,
  infoStylesNoSpace,
  Link,
  linkStylesGhost,
  ListRow,
  Loader,
  loaderStylesSpinnerXLarge,
  useClassNames,
  useToast,
} from '@h2oai/ui-kit';
import { cloneElement, CSSProperties, useCallback, useEffect, useRef, useState } from 'react';

import { fsApi } from '../../featurestore/api';
import {
  CoreService,
  FeatureSetIngest,
  GetIngestHistoryRequest,
  GetIngestHistoryResponse,
} from '../../featurestore/gen/ai/h2o/featurestore/api/v1/CoreService.pb';
import { CommonImageSrc } from '../../featurestore/models';
import { getErrorMessageBarMessage } from '../../featurestore/utils';
import { formatDefaultDateAndTimeWithSeconds } from '../../utils/utils';
import { ListCellImage } from '../ListCellImage/ListCellImage';
import {
  IIngestHistoryListClassNames,
  IIngestHistoryListStyles,
  IngestHistoryListStylesDefault,
} from './IngestHistoryList.styles';

const renderSourceInfo = (source: { [key: string]: any }, hasInfo = true) => {
  const sourceName = source ? Object.keys(source)[0] : '';
  if (sourceName) {
    const sourceInfo = (source as { [key: string]: any })[sourceName];
    return (
      <div key={sourceName} style={{ lineHeight: '18px', display: 'flex', gap: '8px' }}>
        <div key="sourceName">
          <strong>{sourceName}</strong>
        </div>
        {cloneElement(
          hasInfo ? <Info key="info" styles={infoStylesNoSpace} isTooltip /> : <div />,
          undefined,
          <>
            {Object.keys(sourceInfo).map((name) => {
              const val = sourceInfo[name];
              if (val && typeof val === `object`) {
                const newSource: { [key: string]: any } = {};
                newSource[name] = val;
                return renderSourceInfo(newSource, false);
              }
              return (
                <div key={name}>
                  <strong>{name}:</strong> {val}
                </div>
              );
            })}
          </>
        )}
      </div>
    );
  } else {
    return <>-</>;
  }
};

const getColumns = (onRevert: (item: FeatureSetIngest) => void): IColumnWithDataType[] => {
  const columns: IColumnWithDataType[] = [
    {
      key: 'ingestId',
      name: 'Ingest Id',
      fieldName: 'ingestId',
      minWidth: 200,
      data: {
        listCellProps: {
          onRenderPrefix: (item) =>
            item.name?.toLowerCase().includes('h2o') ? (
              <ListCellImage imageSrc={CommonImageSrc.logo512} />
            ) : (
              <ListCellImage iconName="FormLibraryMirrored" />
            ),
        },
      },
    },
    {
      key: 'ingestTimestamp',
      name: 'Ingested At',
      fieldName: 'ingestTimestamp',
      minWidth: 180,
      data: {
        listCellProps: {
          onRenderText: (item: FeatureSetIngestWithInfo) => (
            <>{formatDefaultDateAndTimeWithSeconds(item.ingestTimestamp)}</>
          ),
        },
      },
    },
    {
      key: 'sourceName',
      name: 'Source',
      fieldName: 'sourceName',
      minWidth: 135,
      data: {
        listCellProps: {
          onRenderText: (item) => renderSourceInfo(item.source),
        },
      },
    },
    {
      key: 'featureSetVersion',
      name: 'Feature Set Version',
      fieldName: 'featureSetVersion',
      minWidth: 140,
    },
    {
      key: 'startDateTime',
      name: 'Scope Start At',
      fieldName: 'startDateTime',
      minWidth: 180,
      data: {
        listCellProps: {
          onRenderText: (item: FeatureSetIngestWithInfo) => (
            <>{formatDefaultDateAndTimeWithSeconds(item.startDateTime)}</>
          ),
        },
      },
    },
    {
      key: 'endDateTime',
      name: 'Scope End At',
      fieldName: 'endDateTime',
      minWidth: 180,
      data: {
        listCellProps: {
          onRenderText: (item: FeatureSetIngestWithInfo) => (
            <>{formatDefaultDateAndTimeWithSeconds(item.endDateTime)}</>
          ),
        },
      },
    },
    {
      key: 'parentIngestNames',
      name: 'Parent Ingests',
      fieldName: 'parentIngestNames',
      minWidth: 120,
      data: {
        listCellProps: {
          emptyMessage: '-',
        },
      },
    },
    {
      key: 'ingestedRecordsCount',
      name: 'Ingested Records',
      fieldName: 'ingestedRecordsCount',
      minWidth: 130,
    },
    {
      key: 'starterName',
      name: 'Started By',
      fieldName: 'starterName',
      minWidth: 120,
    },
    {
      key: 'actions',
      name: 'Actions',
      minWidth: 125,
      maxWidth: 125,
      onRender: (item: FeatureSetIngest) => {
        return (
          <div
            style={{
              ...(CommonStyles.flexCenterVertically as CSSProperties),
              alignItems: 'flex-end',
              textAlign: 'right',
              width: '100%',
            }}
          >
            <Link styles={linkStylesGhost} onClick={() => onRevert(item)}>
              Revert
            </Link>
          </div>
        );
      },
    },
  ];
  return columns;
};

interface FeatureSetIngestWithInfo extends FeatureSetIngest {
  starterName?: string;
  startDateTime?: string;
  endDateTime?: string;
  parentIngestNames?: string;
  sourceName?: string;
}

const convertFeatureSetIngestWithInfo = (item: FeatureSetIngest): FeatureSetIngestWithInfo => {
  return {
    ...item,
    starterName: item.startedBy?.name,
    startDateTime: item.scope?.startDateTime,
    endDateTime: item.scope?.endDateTime,
    parentIngestNames: item.parentIngests?.join(', '),
    sourceName: item.source ? Object.keys(item.source)[0] : '-',
  };
};

interface IIngestHistoryListProps {
  reload?: boolean;
  featureSetId?: string;
  featureSetVersion?: string;
  onStartRevert?: () => void;
}

export const IngestHistoryList = ({
  reload,
  featureSetId,
  featureSetVersion,
  onStartRevert,
}: IIngestHistoryListProps) => {
  const [data, setData] = useState<FeatureSetIngest[]>([]);
  const [loadingMessage, setLoadingMessage] = useState<string>('');
  const { addToast } = useToast();
  const refMeta = useRef<{
    mounted?: boolean;
  }>({});
  const load = useCallback(async () => {
    setLoadingMessage(`Loading Feature Set Ingests...`);
    try {
      const req: GetIngestHistoryRequest = {
        featureSetId,
        featureSetVersion,
      };
      const { ingestHistory = [] }: GetIngestHistoryResponse = await fsApi(CoreService.GetIngestHistory, req);
      if (!refMeta.current.mounted) return;
      setData(ingestHistory);
    } catch (err) {
      addToast(getErrorMessageBarMessage(err, `An error has occurred while loading Feature Set Ingests: `));
    } finally {
      if (!refMeta.current.mounted) return;
      setLoadingMessage('');
    }
  }, [addToast]);
  const onRevert = useCallback(
    async (item: FeatureSetIngest) => {
      setLoadingMessage(`Request to revoke the user permission...`);
      try {
        const { featureSetId, featureSetVersion, ingestId } = item;
        await fsApi(CoreService.StartRevertIngestJob, { featureSetId, featureSetVersion, ingestId });
        addToast({
          messageBarType: MessageBarType.success,
          message: `You have successfully started to revert the ingestion`,
        });
        onStartRevert && onStartRevert();
      } catch (err) {
        addToast(getErrorMessageBarMessage(err, `An error has occurred while applying request: `));
      } finally {
        setLoadingMessage('');
      }
    },
    [addToast, load]
  );
  const classNames = useClassNames<IIngestHistoryListStyles, IIngestHistoryListClassNames>(
    'IngestHistoryList',
    IngestHistoryListStylesDefault
  );
  const detailsListProps: IDetailsListProps = {
    styles: detailsListStylesCard,
    headerStyles: detailsHeaderStylesBasic,
    isHeaderVisible: false,
    columns: getColumns(onRevert),
    items: data,
    cellStyleProps: {
      cellExtraRightPadding: 0,
      cellLeftPadding: 0,
      cellRightPadding: 0,
    },
    onRenderRow: (props?: IDetailsRowProps) => {
      const { item, columns = [] } = props || {};
      return <ListRow data={convertFeatureSetIngestWithInfo(item)} columns={columns} />;
    },
  };
  useEffect(() => {
    refMeta.current.mounted = true;
    if (featureSetId && featureSetVersion) {
      load();
    }
    return () => {
      refMeta.current.mounted = false;
    };
  }, [load, featureSetId, featureSetVersion]);
  useEffect(() => {
    reload && load();
  }, [reload]);
  return (
    <div className={classNames.root} data-test="feature-set-ingest-list">
      {loadingMessage ? (
        <Loader styles={loaderStylesSpinnerXLarge} label={loadingMessage} />
      ) : (
        <div className={classNames.cardView} data-is-scrollable="true" data-test="feature-set-ingest-list-card-view">
          {data.length === 0 ? (
            <EmptyMessage message="No Feature Set Ingests Available" />
          ) : (
            <DetailsList {...detailsListProps} />
          )}
        </div>
      )}
    </div>
  );
};
